import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { formatDate, formatDateDDMMYYYY, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'

import { DocumentBatch as DocumentBatchRecord } from '../../../models/DocumentBatch'

import { selectDocumentBatch, selectDocumentBatchContact, selectDocumentBatchCreator } from '../../../store/processDocument/documentBatch'
import { useDeleteDocumentBatchMutation } from '../../../store/ai/documentBatches'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { getInitials } from '../../utils'

import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'

import { DocumentRow } from '../Documents/DocumentRow'
import { DocumentLeaveGuard } from '../DocumentLeaveConfirmationModal'
import { DocumentBatchHeader } from './DocumentBatchHeader'

import './DocumentBatch.scss'
import { User } from '../../../models/User'
import { useNavigate } from 'react-router-dom'

export const DocumentBatch = () => {
  const documentBatch = useAppSelector(selectDocumentBatch)
  const contact = useAppSelector(selectDocumentBatchContact)
  const creator = useAppSelector(selectDocumentBatchCreator)

  const { portalRef } = useContext(HeaderContext)

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  const [show, setShow] = useState<boolean>(false)

  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  if (!documentBatch || !contact || !creator) {
    return null
  }

  const { documents, createdAt } = documentBatch

  const creatorUserDisplayName = getUserDisplayName(creator)

  const showDeleteModal = () => {
    setShow(true)
  }

  const hideDeleteModal = () => {
    setShow(false)
  }

  return (
    <>
      <DocumentLeaveGuard />
      {headerContainer ? createPortal(<DocumentBatchHeader />, headerContainer) : null}
      {show ? <DeleteDocumentBatchModal show={show} onHide={hideDeleteModal} documentBatch={documentBatch} contact={contact} /> : null}
      <div className='d-flex flex-column zocks-gap-3'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column gap-1'>
            <h2>
              {formatDateDDMMYYYY(createdAt)} - {getUserDisplayName(contact)}
            </h2>
            <div className='d-flex align-items-center zocks-gap-0.75 paragraph-medium zocks-text-gray-700'>
              <span>{formatDate(createdAt)}</span>
              <div className='document-batch-header-divider zocks-bg-color-gray-700 rounded-circle' />
              <div className='d-flex align-items-center gap-1'>
                <UserAvatar
                  width='1rem'
                  height='1rem'
                  className='document-batch-creator-initials fw-bolder zocks-bg-color-gray-700 zocks-text-white'
                  useInitials
                  initialLetters={getInitials(creatorUserDisplayName)}
                />
                <span className=''>{creatorUserDisplayName}</span>
              </div>
            </div>
          </div>
          <Button variant='header' onClick={showDeleteModal}>
            Delete all
          </Button>
        </div>
        <div className='d-flex flex-column gap-3'>
          {documents.map((document) => (
            <DocumentRow key={document.id} document={document} />
          ))}
        </div>
      </div>
    </>
  )
}

interface DeleteDocumentBatchModalProps {
  show: boolean
  onHide: () => void
  documentBatch: DocumentBatchRecord
  contact: User
}

const DeleteDocumentBatchModal: React.FC<DeleteDocumentBatchModalProps> = ({ show, onHide, documentBatch, contact }) => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()

  const [triggerDeleteDocumentBatch, { isLoading: deleteLoading }] = useDeleteDocumentBatchMutation()

  const handleDelete = async () => {
    try {
      await triggerDeleteDocumentBatch({ accountId, documentBatchId: documentBatch.id }).unwrap()

      navigate('/documents/upload')
    } catch (e) {
      console.error(e)
    }
  }

  const { documents, createdAt } = documentBatch

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={
        <BootstrapModal.Title>
          Delete {documents.length} {documents.length > 1 ? 'documents' : 'document'}
        </BootstrapModal.Title>
      }
      headerBodySeparator={true}
      bodyClassName='zocks-pb-0.75'
      body={
        <span>
          By deleting{' '}
          <strong>
            {formatDateDDMMYYYY(createdAt)} - {getUserDisplayName(contact)}
          </strong>{' '}
          package, all belonging files ({documents.length}) and their summaries will be deleted and all unsynchronized insights will be permanently lost.
        </span>
      }
      footerClassName='zocks-pt-0.75 zocks-px-1.5 zocks-pb-1.5'
      showFooterActions={true}
      onFooterPrimaryAction={handleDelete}
      footerPrimaryActionLoading={deleteLoading}
      footerPrimaryActionTitle='Delete'
      footerPrimaryActionButtonVariant='danger'
    />
  )
}
